/* ==========================================================================
   Bauer & Meyer — product.js
   Product page components. Loaded with `defer` by the product sections; safe to
   include more than once (the guard below makes the second run a no-op).

   Custom elements: <variant-picker>, <product-gallery>, <bundle-offers>,
   <sticky-atc>, <product-recommendations>. (<delivery-estimate> lives in theme.js: the cart uses it too.)
   Section events (dispatched on [data-product-section]):
   - product:variant-change  { variant }   — right after a picker change
   - product:rendered        { variant }   — after Liquid regions are refreshed
   - product:offer-change    { variantId, priceHtml }
   ========================================================================== */

(() => {
  if (customElements.get('product-gallery')) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const strings = () => (window.theme && window.theme.strings) || {};
  const sectionOf = (el) => el.closest('[data-product-section]');
  const emit = (el, name, detail) => el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));

  /* ---------- Shared: buy button state ---------- */
  function setBuyButtons(section, available, label) {
    section.querySelectorAll('[data-atc]').forEach((button) => {
      button.disabled = !available;
      const text = button.querySelector('[data-atc-label]');
      if (text && label) text.textContent = label;
    });
  }

  /* ---------- Shared: switch variant + refresh Liquid regions ----------
     Every element marked [data-product-region="name"] gets the inner HTML of
     its fresh counterpart from `?variant=ID&section_id=…`, so Liquid stays the
     single source of truth for price, stock and buttons. */
  async function selectVariant(section, variant) {
    if (!variant) {
      setBuyButtons(section, false, strings().unavailable);
      emit(section, 'product:variant-change', { variant: null });
      return;
    }

    const input = section.querySelector('[data-variant-input]');
    if (input) input.value = variant.id;
    setBuyButtons(section, variant.available, variant.available ? strings().addToCart : strings().soldOut);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url.toString());

    const gallery = section.querySelector('product-gallery');
    if (gallery && variant.media) gallery.goTo(variant.media);

    emit(section, 'product:variant-change', { variant });

    if (section.request) section.request.abort();
    section.request = new AbortController();
    section.classList.add('is-updating');
    try {
      const res = await fetch(`${section.dataset.url}?variant=${variant.id}&section_id=${section.dataset.sectionId}`, { signal: section.request.signal });
      const doc = parse(await res.text());
      section.querySelectorAll('[data-product-region]').forEach((current) => {
        const fresh = doc.querySelector(`[data-product-region="${current.dataset.productRegion}"]`);
        if (fresh) current.innerHTML = fresh.innerHTML;
      });
      // Option availability depends on the selected combination
      doc.querySelectorAll('[data-option-value]').forEach((fresh) => {
        const current = document.getElementById(fresh.id);
        if (current) current.toggleAttribute('data-unavailable', fresh.hasAttribute('data-unavailable'));
      });
      section.classList.remove('is-updating');
      emit(section, 'product:rendered', { variant });
    } catch (err) {
      if (err.name !== 'AbortError') window.location.assign(url.toString());
    }
  }

  /* ---------- <variant-picker> ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = sectionOf(this);
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.addEventListener('change', () => this.onChange());
    }

    onChange() {
      const selected = Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
        const input = fieldset.querySelector('input:checked');
        const output = fieldset.querySelector('[data-selected-value]');
        if (input && output) output.textContent = input.value;
        return input ? input.value : null;
      });
      const variant = this.variants.find((v) => v.options.every((option, i) => option === selected[i]));
      if (this.section) selectVariant(this.section, variant);
    }
  }

  /* ---------- <product-gallery> ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.thumbList = this.querySelector('[data-thumbs]');
      this.counter = this.querySelector('[data-counter]');
      this.dialog = this.querySelector('dialog');
      this.index = 0;

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.onScroll());
      }, { passive: true });

      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        const zoom = e.target.closest('[data-zoom]');
        const video = e.target.closest('[data-load-video]');
        if (thumb) this.goTo(thumb.dataset.thumb);
        else if (e.target.closest('[data-gallery-prev]')) this.step(-1);
        else if (e.target.closest('[data-gallery-next]')) this.step(1);
        else if (video) this.loadVideo(video);
        else if (zoom) this.openZoom(zoom.dataset.zoom);
        else if (e.target.closest('[data-zoom-close]')) this.dialog.close();
      });

      if (this.dialog) {
        this.dialog.addEventListener('close', () => window.theme && window.theme.lockScroll && window.theme.lockScroll(false));
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      }
      if (this.querySelector('model-viewer')) this.loadModels();
    }

    get slides() { return Array.from(this.track.children); }
    get isGrid() { return this.dataset.layout === 'grid' && desktop.matches; }

    step(dir) {
      const slides = this.slides;
      const next = slides[Math.max(0, Math.min(slides.length - 1, this.index + dir))];
      if (next) this.goTo(next.dataset.mediaId);
    }

    goTo(mediaId, smooth = true) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      if (this.isGrid) {
        // Stacked grid: bring the variant image to the top instead of scrolling the page
        if (slide !== this.track.firstElementChild) this.track.prepend(slide);
        return;
      }
      this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
    }

    onScroll() {
      const width = this.track.clientWidth || 1;
      const index = Math.round(this.track.scrollLeft / width);
      if (index === this.index) return;
      this.index = index;
      const active = this.slides[index];
      if (!active) return;
      this.pauseMedia(active);

      if (this.counter) this.counter.textContent = index + 1;
      this.querySelectorAll('[data-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      this.querySelectorAll('[data-thumb]').forEach((thumb) => {
        const current = thumb.dataset.thumb === active.dataset.mediaId;
        thumb.setAttribute('aria-current', current ? 'true' : 'false');
        if (current && this.thumbList) {
          const list = this.thumbList;
          const top = thumb.offsetTop - list.clientHeight / 2 + thumb.clientHeight / 2;
          list.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      });
    }

    pauseMedia(except) {
      this.slides.forEach((slide) => {
        if (slide === except) return;
        slide.querySelectorAll('video').forEach((v) => v.pause());
        slide.querySelectorAll('iframe').forEach((frame) => {
          const msg = frame.src.includes('youtube') ? '{"event":"command","func":"pauseVideo","args":""}' : '{"method":"pause"}';
          if (frame.contentWindow) frame.contentWindow.postMessage(msg, '*');
        });
      });
    }

    loadVideo(button) {
      const template = button.parentElement.querySelector('template');
      if (!template) return;
      const content = template.content.cloneNode(true);
      button.replaceWith(content);
    }

    openZoom(mediaId) {
      if (!this.dialog) return;
      this.dialog.showModal();
      if (window.theme && window.theme.lockScroll) window.theme.lockScroll(true);
      const target = this.dialog.querySelector(`[data-media-id="${mediaId}"]`);
      if (target) target.scrollIntoView({ block: 'start' });
    }

    loadModels() {
      const shopify = window.Shopify;
      if (!shopify || !shopify.loadFeatures) return;
      shopify.loadFeatures([{
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors) return;
          this.querySelectorAll('model-viewer').forEach((model) => new shopify.ModelViewerUI(model));
        },
      }]);
      const json = this.querySelector('[data-models-json]');
      if (!json) return;
      const setupXR = () => {
        if (!window.ShopifyXR) { document.addEventListener('shopify_xr_initialized', setupXR, { once: true }); return; }
        window.ShopifyXR.addModels(JSON.parse(json.textContent));
        window.ShopifyXR.setupXRElements();
      };
      shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: (errors) => { if (!errors) setupXR(); } }]);
    }
  }

  /* ---------- <bundle-offers>: "Los" vs Duo/Collection cards ---------- */
  class BundleOffers extends HTMLElement {
    connectedCallback() {
      this.section = sectionOf(this);
      if (!this.section) return;
      this.single = this.querySelector('[data-offer="single"]');
      this.addEventListener('change', () => this.apply());
      this.onVariant = (e) => {
        const { variant } = e.detail;
        if (this.single) {
          this.single.dataset.available = variant ? String(variant.available) : 'false';
          if (variant) this.single.value = variant.id;
        }
        // Set cards follow the chosen colour (first option) when the set has that value
        if (variant && variant.options) {
          this.querySelectorAll('[data-variant-by-option]').forEach((input) => {
            let map = {};
            try { map = JSON.parse(input.dataset.variantByOption); } catch (err) { return; }
            const match = map[variant.options[0]];
            if (match) { input.value = match[0]; input.dataset.available = String(match[1]); }
          });
        }
        // An unavailable combination is already handled by selectVariant
        if (variant || !(this.single && this.single.checked)) this.apply();
      };
      this.onRendered = () => this.apply();
      this.section.addEventListener('product:variant-change', this.onVariant);
      this.section.addEventListener('product:rendered', this.onRendered);
      // Browser may restore a non-default card on back/forward navigation
      const checked = this.querySelector('input:checked');
      if (checked && checked !== this.single) this.apply();
    }

    disconnectedCallback() {
      if (!this.section) return;
      this.section.removeEventListener('product:variant-change', this.onVariant);
      this.section.removeEventListener('product:rendered', this.onRendered);
    }

    apply() {
      const input = this.querySelector('input:checked');
      if (!input) return;
      const target = this.section.querySelector('[data-variant-input]');
      if (target) target.value = input.value;
      const available = input.dataset.available !== 'false';
      setBuyButtons(this.section, available, available ? strings().addToCart : strings().soldOut);
      const card = input.closest('[data-offer-card]');
      const price = card && card.querySelector('[data-offer-price]');
      // The main price follows the chosen card (the card price is re-rendered by Liquid on variant changes)
      const mainPrice = this.section.querySelector('[data-main-price]');
      if (mainPrice && price) {
        mainPrice.innerHTML = price.innerHTML.replace('class="price', 'class="price price--large');
      }
      // Saving and gifts of the chosen set appear under the price; "Los" clears them
      const perks = this.section.querySelector('[data-offer-perks]');
      if (perks) {
        const source = card && card.querySelector('template[data-offer-perks-src]');
        perks.innerHTML = source ? source.innerHTML : '';
      }
      emit(this.section, 'product:offer-change', { variantId: input.value, priceHtml: price ? price.innerHTML : '' });
    }
  }

  /* ---------- <sticky-atc> ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = sectionOf(this);
      const anchor = this.section && this.section.querySelector('[data-atc-anchor]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      this.past = false;
      this.footerVisible = false;
      this.observers = [new IntersectionObserver(([entry]) => {
        this.past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggle();
      })];
      this.observers[0].observe(anchor);
      const footer = document.querySelector('.shopify-section-group-footer-group, footer');
      if (footer) {
        this.observers.push(new IntersectionObserver(([entry]) => { this.footerVisible = entry.isIntersecting; this.toggle(); }));
        this.observers[1].observe(footer);
      }
      this.onOffer = (e) => {
        const price = this.querySelector('[data-sticky-price]');
        if (price && e.detail.priceHtml) price.innerHTML = e.detail.priceHtml;
      };
      this.section.addEventListener('product:offer-change', this.onOffer);
    }

    disconnectedCallback() {
      (this.observers || []).forEach((o) => o.disconnect());
      if (this.section && this.onOffer) this.section.removeEventListener('product:offer-change', this.onOffer);
      document.body.classList.remove('has-sticky-atc');
    }

    toggle() {
      const show = this.past && !this.footerVisible;
      this.classList.toggle('is-visible', show);
      this.inert = !show;
      document.body.classList.toggle('has-sticky-atc', show);
    }
  }

  /* ---------- <product-recommendations data-url="…&section_id=…"> ----------
     Fetches the section from the recommendations endpoint once it nears the
     viewport and swaps in the element with the same id. */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      if (!('IntersectionObserver' in window)) { this.load(); return; }
      this.observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 600px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }

    async load() {
      this.loaded = true;
      try {
        const res = await fetch(this.dataset.url);
        const fresh = parse(await res.text()).getElementById(this.id);
        if (fresh && fresh.querySelector('[data-has-results]')) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
        } else if (!window.Shopify || !window.Shopify.designMode) {
          this.hidden = true;
        }
      } catch (e) {
        this.hidden = true;
      }
    }
  }

  /* ---------- Plain helpers (no element of their own) ---------- */

  // Sticky info column: sticks at the top, or at the bottom when taller than the viewport
  function initStickyInfo() {
    if (!('ResizeObserver' in window)) return;
    document.querySelectorAll('[data-sticky-info]').forEach((el) => {
      if (el.resizeObserver) return;
      el.resizeObserver = new ResizeObserver(() => el.style.setProperty('--info-height', `${el.offsetHeight}px`));
      el.resizeObserver.observe(el);
    });
  }

  // Muted loop videos play only while visible (and never with reduced motion)
  function initInViewVideos() {
    const videos = document.querySelectorAll('video[data-inview-autoplay]:not([data-observed])');
    if (!videos.length) return;
    if (reducedMotion() || !('IntersectionObserver' in window)) {
      videos.forEach((v) => { v.controls = true; v.dataset.observed = ''; });
      return;
    }
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) entry.target.play().catch(() => {});
      else entry.target.pause();
    }), { threshold: 0.25 });
    videos.forEach((v) => { v.dataset.observed = ''; io.observe(v); });
  }

  // Steps: an accordion on mobile, all open (non-interactive) on desktop
  function syncSteps() {
    document.querySelectorAll('[data-steps] details').forEach((details, i) => {
      const summary = details.querySelector('summary');
      if (desktop.matches) {
        details.open = true;
        if (summary) summary.tabIndex = -1;
      } else {
        if (summary) summary.removeAttribute('tabindex');
        if (details.dataset.synced !== 'mobile') details.open = i === 0 || details.closest('[data-steps]').dataset.openAll === 'true';
      }
      details.dataset.synced = desktop.matches ? 'desktop' : 'mobile';
    });
  }
  document.addEventListener('click', (e) => {
    if (desktop.matches && e.target.closest('[data-steps] summary')) e.preventDefault();
  });

  /* ---------- <product-tabs>: description / specifications, long description folded ---------- */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
      this.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
        const i = this.tabs.indexOf(document.activeElement);
        if (i < 0) return;
        const next = this.tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
        this.select(next);
        next.focus();
      });
      // Fold the description only when it is clearly longer than the folded height (~7 lines)
      const fold = this.querySelector('[data-fold]');
      const toggle = this.querySelector('[data-fold-toggle]');
      if (fold && toggle && fold.scrollHeight > 200) {
        fold.classList.add('is-folded');
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          const open = fold.classList.toggle('is-folded') === false;
          toggle.setAttribute('aria-expanded', String(open));
          toggle.querySelector('[data-more]').hidden = open;
          toggle.querySelector('[data-less]').hidden = !open;
        });
      } else if (fold) {
        fold.classList.add('is-short');
      }
    }
    select(tab) {
      this.tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected));
        t.tabIndex = selected ? 0 : -1;
        const panel = this.querySelector(`#${t.getAttribute('aria-controls')}`);
        if (panel) panel.hidden = !selected;
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
  customElements.define('product-tabs', ProductTabs);
  customElements.define('product-gallery', ProductGallery);
  customElements.define('bundle-offers', BundleOffers);
  customElements.define('sticky-atc', StickyAtc);
  customElements.define('product-recommendations', ProductRecommendations);

  const init = () => { initStickyInfo(); initInViewVideos(); syncSteps(); };
  init();
  desktop.addEventListener('change', syncSteps);
  document.addEventListener('shopify:section:load', init);
})();
